<!-- app-subdomain-creators/src/Components/NewsfeedCreatorScheduledPost.vue -->

<template>
  <div class="relative mt-4 w-full rounded-xl border border-zinc-200 bg-[#fff] shadow-sm">

        <!-- 🔹 Pinned indicator + saved bookmark + menu for SCHEDULED post -->
    <div class="absolute top-[10px] right-4 flex items-center gap-1">
      <!-- Pinned indicator (blue pin icon) -->
      <div
        v-if="isPinned"
        class="relative group"
      >
        <img
          :src="pinPlatformaIcon"
          alt="Pinned post"
          class="w-5 h-5 mr-1"
        />

        <p
          class="pointer-events-none absolute px-2 py-1.5 text-[11px] rounded-xl
                 bg-black/70 text-white whitespace-nowrap z-50 top-7 left-1/2
                 -translate-x-1/2 shadow-lg
                 opacity-0 translate-y-1
                 group-hover:opacity-100 group-hover:translate-y-0
                 transition-all duration-150"
        >
          Pinned Post
        </p>
      </div>

      <!-- Orange bookmark when this scheduled post is saved -->
      <div
        v-if="isPostSaved"
        class="relative group"
      >
        <BookmarkIcon class="w-6 text-[#ffae00]" />

        <!-- Tooltip -->
        <p
          class="pointer-events-none absolute px-2 py-1.5 text-[11px] rounded-xl
                 bg-black/70 text-white whitespace-nowrap z-50 top-7 left-1/2
                 -translate-x-1/2 shadow-lg
                 opacity-0 translate-y-1
                 group-hover:opacity-100 group-hover:translate-y-0
                 transition-all duration-150"
        >
          Saved Post
        </p>
      </div>

      <!-- Post menu (with schedule actions enabled) -->
      <NewsfeedCreatorPostMenu
        :post-id="post.id"
        :is-saved="isPostSaved"
        :is-pinned="isPinned"
        :in-saved-tab="isSavedContext"
        :is-scheduled="true"
        @save-post="handleSavePost"
        @unsave-post="handleUnsavePost"
        @pin-post="handleTogglePinned"
        @edit-post="(payload) => $emit('edit-post', payload)"
        @delete-post="forwardDeletePost"
        @toggle-commenting="(payload) => $emit('toggle-commenting', payload)"
        @reschedule-post="() => $emit('reschedule', post)"
        @post-now="() => $emit('post-now', post)"
      />
    </div>


    <!-- Header: avatar, name, scheduled time -->
    <div class="px-4 pt-3 pb-2 flex items-start justify-between">
      <div class="flex items-start gap-3">
        <img
          :src="authorAvatarSrc"
          alt="Author avatar"
          class="h-10 w-10 rounded-full object-cover"
        />

        <div>
          <p class="text-sm font-semibold text-zinc-900 -mb-1">
            {{ authorName }}
          </p>

          <div class="flex flex-wrap items-center gap-x-3 gap-y-1">
            <!-- Scheduled time under name with hover tooltip -->
            <div class="group/time flex items-center gap-1">
              <div class="relative inline-block">
                <p class="text-xs font-medium text-[#2D8CFF] cursor-pointer hover:underline mt-[2px]">
                  Scheduled Release {{ displayScheduledTime }}
                </p>

                <!-- Pretty tooltip with full datetime -->
                <p
                  class="pointer-events-none absolute hidden group-hover/time:block px-2 py-4 text-xs rounded-2xl
                        bg-black/75 text-white whitespace-normal z-50 top-7 left-0 shadow-lg w-64 text-center"
                >
                  {{ scheduledFullTimeTooltip }}
                </p>
              </div>

              <ClockIcon class="w-5 text-[#2D8CFF] items-center" />
            </div>

            <div
              v-if="scheduledDeleteDate"
              class="group/delete-time flex items-center gap-1"
            >
              <div class="relative inline-block">
                <p class="mt-[2px] cursor-pointer text-xs font-medium text-red-500 hover:underline">
                  Scheduled Delete {{ displayScheduledDeleteTime }}
                </p>
                <p
                  class="pointer-events-none absolute hidden group-hover/delete-time:block px-2 py-4 text-xs rounded-2xl
                        bg-black/75 text-white whitespace-normal z-50 top-7 left-0 shadow-lg w-64 text-center"
                >
                  {{ scheduledDeleteFullTimeTooltip }}
                </p>
              </div>

              <ClockIcon class="w-5 text-red-500" />
            </div>
          </div>



        </div>
      </div>
    </div>

    <!-- Body text (same formatting behavior as NewsfeedCreatorPost) -->
    <div v-if="rawBody" :class="hasTextBackground ? 'px-0 pb-3' : 'px-4 pb-3'">
      <div
        :class="hasTextBackground ? 'aspect-[16/9] min-h-[340px] flex items-center justify-center text-center px-8 py-6 bg-cover bg-center bg-no-repeat' : ''"
        :style="textBackgroundStyle"
      >
        <p
          :class="[
            bodyTextClass,
            'leading-snug whitespace-pre-line break-words',
            hasTextBackground ? backgroundTextToneClass : 'text-zinc-800'
          ]"
        >
          <span v-html="displayBodyHtml"></span>
          <button
            v-if="isBodyLong && !hasTextBackground"
            type="button"
            :class="hasTextBackground ? `inline text-sm font-semibold ml-1 hover:underline ${backgroundTextButtonClass}` : 'inline text-sm font-semibold text-zinc-800 hover:text-zinc-900 ml-1 hover:underline'"
            @click.stop="showFullBody = !showFullBody"
          >
            {{ showFullBody ? 'Show less' : 'See more' }}
          </button>
        </p>
      </div>
    </div>

    <NewsfeedPostPollCard
      v-if="hasPoll"
      :post-id="post.id"
      :post="post"
      :api-proxy-url="apiProxyUrl"
      :can-manage="true"
      :read-only="true"
    />

    <!-- Optional media -->
    <div v-if="hasMedia && !hasPoll" class="pb-3 pt-2">
      <!-- Single video -->
      <div
        v-if="displayMediaItems.length === 1 && displayMediaItems[0].type === 'video'"
        class="w-full bg-black flex items-center justify-center overflow-hidden"
      >
        <div class="w-full">
          <NewsfeedInlineVideoPlayer
            :file-id="displayMediaItems[0].fileId ? String(displayMediaItems[0].fileId) : null"
            :feature-instance-id="post?.featureInstanceId || post?.instanceId || post?.instance_id || null"
            :src="displayMediaItems[0].src"
            :poster="displayMediaItems[0].thumbnailSrc || displayMediaItems[0].poster || null"
            :autoplay="true"
            :muted="true"
            :max-height="680"
            @open-viewer="openViewerAt(0)"
          />
        </div>
      </div>

      <!-- Single image -->
      <div
        v-else-if="displayMediaItems.length === 1"
        class="w-full bg-black flex items-center justify-center overflow-hidden cursor-pointer"
        @click="openViewerAt(0)"
      >
        <img
          :src="displayMediaItems[0].src"
          alt="Scheduled media"
          class="max-h-[60vh] max-w-full w-auto object-contain"
        />
      </div>

      <!-- Multiple media -->
      <div v-else class="w-full bg-black flex items-center justify-center overflow-hidden">
        <div class="w-full max-h-[60vh] overflow-hidden">
          <div
            class="grid w-full h-full gap-[3px] bg-white"
            :class="displayGridClass"
          >
            <div
              v-for="(item, idx) in displayMediaItems"
              :key="idx"
              :class="['bg-black cursor-pointer', displayTileClass(idx)]"
              @click="handleMediaTileClick(item, idx)"
            >
              <NewsfeedInlineVideoPlayer
                v-if="item.type === 'video' && isInlinePlayingVideoTile(idx)"
                :file-id="item.fileId ? String(item.fileId) : null"
                :feature-instance-id="post?.featureInstanceId || post?.instanceId || post?.instance_id || null"
                :src="item.src"
                :poster="item.thumbnailSrc || item.poster || null"
                :autoplay="true"
                :muted="true"
                :max-height="680"
                :fill-container="true"
                @open-viewer="openViewerAt(idx)"
              />

              <img
                v-else
                :src="item.type === 'video' ? (item.thumbnailSrc || item.src) : item.src"
                alt="Scheduled media"
                :class="['w-full h-full', multiImageFitClass]"
              />

              <div
                v-if="item.type === 'video' && !isInlinePlayingVideoTile(idx)"
                class="absolute inset-0 flex items-center justify-center pointer-events-none"
              >
                <div class="rounded-full bg-black/60 p-2">
                  <PlayIcon class="w-6 h-6 text-white" />
                </div>
              </div>

              <button
                v-if="item.type === 'video' && !isInlinePlayingVideoTile(idx)"
                type="button"
                class="absolute inset-0 z-10"
                @click.stop="playInlineVideoAt(idx)"
                aria-label="Play video"
              ></button>

              <!-- +N overlay on last tile -->
              <div
                v-if="idx === displayMediaItems.length - 1 && extraMediaCount > 0"
                class="absolute inset-0 bg-black/45 flex items-center justify-center cursor-pointer"
              >
                <span class="text-white text-2xl font-semibold">
                  +{{ extraMediaCount }}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Footer buttons: Reschedule / Post now -->
    <div class=" px-4 py-4 flex gap-2">
      <button
        type="button"
        class="flex-1 text-sm font-medium py-2 rounded-md bg-zinc-100 hover:bg-zinc-200 text-zinc-800 transition-colors"
        @click="$emit('reschedule', post)"
      >
        Reschedule Post
      </button>

      <button
        type="button"
        class="flex-1 text-sm font-medium py-2 rounded-md bg-zinc-100 hover:bg-zinc-200 text-zinc-800 transition-colors"
        :disabled="isPublishing"
        @click="$emit('post-now', post)"
      >
        {{ isPublishing ? 'Posting…' : 'Post now' }}
      </button>
    </div>
  </div>

<NewsFeedMediaPostViewer
    :show="viewerOpen"
    :media-items="viewerMediaItems"
    :initial-index="viewerIndex"
    :post="post"
    :author-avatar-src="authorAvatarSrc"
    :commenter-avatar-src="commenterAvatarSrc"
    :display-time="displayScheduledTime"
    @close="viewerOpen = false"
  />

</template>

<script setup>

import { computed, defineProps, ref, watch } from 'vue';
import { ClockIcon } from '@heroicons/vue/24/outline';
import { BookmarkIcon, PlayIcon } from '@heroicons/vue/24/solid';
import NewsFeedMediaPostViewer from '@/Components/NewsFeedMediaPostViewer.vue';
import NewsfeedCreatorPostMenu from '@/Components/NewsfeedCreatorPostMenu.vue';
import NewsfeedInlineVideoPlayer from '@/Components/NewsfeedInlineVideoPlayer.vue';
import NewsfeedPostPollCard from '@/Components/NewsfeedPostPollCard.vue';
import {
  detectNewsfeedBackgroundTone,
  getNewsfeedBackgroundById,
  guessNewsfeedBackgroundTone,
} from '@/utils/newsfeedPostBackgrounds';

// ⬇️ BFF proxy base URL (for image URLs)
const apiProxyUrl = process.env.VUE_APP_API_PROXY_URL;

const emit = defineEmits([
  'save-post',
  'unsave-post',
  'pin-post',
  'edit-post',
  'delete-post',
  'toggle-commenting',
  'reschedule',
  'post-now',
]);

const props = defineProps({
  post: { type: Object, required: true },
  // parent can flip this while doing the publish API call
  isPublishing: { type: Boolean, default: false },
  // whether we are rendering in a "Saved posts" context
  isSavedContext: { type: Boolean, default: false },
});

const isPostSaved = computed(() => {
  const p = props.post || {};
  if (typeof p.isSaved === 'boolean') return p.isSaved;
  if (typeof p.is_saved === 'boolean') return p.is_saved;
  if (typeof p.saved === 'boolean') return p.saved;
  return false;
});

const defaultAuthorAvatar = new URL(
  '@/assets/media/images/default-avatar.png',
  import.meta.url
).href;

const pinPlatformaIcon = new URL(
  '@/assets/media/images/pin-platforma-icon.svg',
  import.meta.url
).href;


const authorName = computed(() => {
  return (
    props.post.postedAsName ||
    props.post.authorName ||
    props.post.currentUserName ||
    'You'
  );
});

const authorAvatarSrc = computed(() => {
  return (
    props.post.postedAsAvatarUrl ||
    props.post.authorAvatar ||
    defaultAuthorAvatar
  );
});

const commenterAvatarSrc = computed(() => authorAvatarSrc.value);

/* ─────────────────────────────────────────────
 * Body formatting (mirrors NewsfeedCreatorPost)
 * ───────────────────────────────────────────*/
const rawBody = computed(() => props.post.body || props.post.content || '');
const backgroundKey = computed(() => {
  return (
    props.post?.backgroundKey ||
    props.post?.backgroundColor ||
    props.post?.background_color ||
    null
  );
});
const resolvedBackground = computed(() => getNewsfeedBackgroundById(backgroundKey.value));
const hasAnyAttachedMedia = computed(() => {
  const atts = Array.isArray(props.post?.attachments) ? props.post.attachments : [];
  const coverMediaFileId =
    props.post?.coverMediaFileId ||
    props.post?.cover_media_file_id ||
    null;
  return atts.length > 0 || !!coverMediaFileId;
});
const hasTextBackground = computed(
  () => !!resolvedBackground.value && !hasPoll.value && !hasAnyAttachedMedia.value
);
const textBackgroundStyle = computed(() => {
  if (!hasTextBackground.value) return {};
  return {
    backgroundImage: `url(${resolvedBackground.value.backgroundUrl})`,
  };
});
const textBackgroundTone = ref('dark');
const backgroundTextToneClass = computed(() => {
  return textBackgroundTone.value === 'light'
    ? 'text-[#1f2937] font-semibold drop-shadow-[0_1px_8px_rgba(255,255,255,0.35)]'
    : 'text-white font-semibold drop-shadow-[0_1px_8px_rgba(0,0,0,0.45)]';
});
const backgroundTextButtonClass = computed(() => {
  return textBackgroundTone.value === 'light' ? 'text-[#1f2937]' : 'text-white';
});

const hasPoll = computed(() => {
  return !!(
    props.post?.poll ||
    props.post?.pollData ||
    props.post?.postPoll ||
    props.post?.postType === 'poll' ||
    props.post?.post_type === 'poll' ||
    Array.isArray(props.post?.pollOptions)
  );
});

watch(
  () => resolvedBackground.value?.backgroundUrl || '',
  async (url) => {
    if (!url) {
      textBackgroundTone.value = 'dark';
      return;
    }
    textBackgroundTone.value = guessNewsfeedBackgroundTone(url);
    textBackgroundTone.value = await detectNewsfeedBackgroundTone(url);
  },
  { immediate: true }
);

const showFullBody = ref(false);

const PREVIEW_MAX_LINES = 2;
const PREVIEW_MAX_CHARS = 140;

const bodyPreviewText = computed(() => {
  const body = rawBody.value;
  if (!body) return '';

  const lines = body.split(/\r?\n/);

  let result = '';
  let linesUsed = 0;

  for (let i = 0; i < lines.length && linesUsed < PREVIEW_MAX_LINES; i++) {
    const line = lines[i];
    const prefix = linesUsed === 0 ? '' : '\n';

    const projectedLength = result.length + prefix.length + line.length;

    if (projectedLength > PREVIEW_MAX_CHARS) {
      const remaining = PREVIEW_MAX_CHARS - result.length - prefix.length;
      if (remaining > 0) result += prefix + line.slice(0, remaining);
      linesUsed++;
      break;
    } else {
      result += prefix + line;
      linesUsed++;
    }
  }

  // Trim trailing whitespace/newlines
  result = result.replace(/[\s\n]+$/g, '');

  if (result.length < body.length) return `${result}…`;
  return result;
});

const isBodyLong = computed(() => {
  if (hasTextBackground.value) return false;
  return bodyPreviewText.value.length < rawBody.value.length;
});

const displayBodyText = computed(() => {
  const body = rawBody.value;
  if (hasTextBackground.value) return body;
  if (!isBodyLong.value || showFullBody.value) return body;
  return bodyPreviewText.value;
});

function escapeHtml(raw) {
  return String(raw || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function linkifyText(raw) {
  const escaped = escapeHtml(raw);
  const urlRegex = /((https?:\/\/|www\.)[^\s<]+)/gi;

  return escaped.replace(urlRegex, (fullMatch) => {
    let match = fullMatch;
    let trailing = '';
    while (/[),.!?:;]+$/.test(match)) {
      trailing = match.slice(-1) + trailing;
      match = match.slice(0, -1);
    }

    const href = match.startsWith('www.') ? `https://${match}` : match;
    return `<a href="${href}" target="_blank" rel="noopener noreferrer" class="text-[#2D8CFF] font-medium hover:underline break-all">${match}</a>${trailing}`;
  });
}

const displayBodyHtml = computed(() => linkifyText(displayBodyText.value));

// Dynamic font size like the main post
const bodyTextClass = computed(() => {
  const body = rawBody.value;
  if (body.length > 0 && body.length < 85) return 'text-[22px]';
  return 'text-[15px]';
});

/* ─────────────────────────────────────────────
 * Scheduled time formatting (relative + tooltip)
 *   ⬇️ UPDATED TO TREAT scheduledFor AS UTC
 * ───────────────────────────────────────────*/

/**
 * Parse a SQL-style UTC datetime string from backend
 *   "YYYY-MM-DD HH:mm:ss" (UTC)
 * into a Date that represents that instant,
 * then the browser will render it in local time.
 */
function parseUtcSqlDatetime(src) {
  if (!src || typeof src !== 'string') return null;

  const trimmed = src.trim();
  if (!trimmed) return null;

  let iso;

  // If backend ever sends ISO-ish, just normalize
  if (trimmed.endsWith('Z') || trimmed.includes('+')) {
    iso = trimmed.replace(' ', 'T');
  } else {
    // Our case: "YYYY-MM-DD HH:mm:ss" is UTC
    iso = trimmed.replace(' ', 'T') + 'Z';
  }

  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d;
}

const scheduledDate = computed(() => {
  const src = props.post.scheduledFor || props.post.scheduled_for;
  return parseUtcSqlDatetime(src);
});

const scheduledDeleteDate = computed(() => {
  const src = props.post.scheduledDeleteAt || props.post.scheduled_delete_at;
  return parseUtcSqlDatetime(src);
});

function formatScheduledTime(d) {
  if (!d) return '';

  const now = new Date();
  const diffMs = d.getTime() - now.getTime();

  // If it's already past for some reason, just show the short absolute time
  if (diffMs <= 0) {
    return formatShortDate(d);
  }

  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHours = Math.floor(diffMin / 60);

  if (diffMin < 1) return 'in 1 minute';
  if (diffMin < 60) return `in ${diffMin} minutes`;

  if (diffHours < 24) {
    const roundedHours = Math.round(diffHours);
    return `in ${roundedHours} hour${roundedHours === 1 ? '' : 's'}`;
  }

  // 24h+ away → show absolute datetime in LOCAL time
  return formatShortDate(d);
}

const displayScheduledTime = computed(() => formatScheduledTime(scheduledDate.value));

const displayScheduledDeleteTime = computed(() => (
  formatScheduledTime(scheduledDeleteDate.value)
));

const scheduledFullTimeTooltip = computed(() => {
  const d = scheduledDate.value;
  if (!d) return '';
  // Local, human-friendly full string
  return formatFullDate(d);
});

const scheduledDeleteFullTimeTooltip = computed(() => {
  const d = scheduledDeleteDate.value;
  if (!d) return '';
  return formatFullDate(d);
});

/**
 * Shared helpers (same style as main post)
 */
function formatShortDate(d) {
  const months = [
    'January','February','March','April','May','June',
    'July','August','September','October','November','December',
  ];
  const month = months[d.getMonth()];
  const day = d.getDate();
  const { hour12, minutes, ampm } = formatTimeParts(d);
  return `${month} ${day} at ${hour12}:${minutes} ${ampm}`;
}

function formatFullDate(d) {
  const weekdays = [
    'Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday',
  ];
  const months = [
    'January','February','March','April','May','June',
    'July','August','September','October','November','December',
  ];

  const weekday = weekdays[d.getDay()];
  const month = months[d.getMonth()];
  const day = d.getDate();
  const year = d.getFullYear();
  const { hour12, minutes, ampm } = formatTimeParts(d);

  return `${weekday}, ${month} ${day}, ${year} at ${hour12}:${minutes} ${ampm}`;
}

function formatTimeParts(d) {
  let hours = d.getHours();
  const minutes = String(d.getMinutes()).padStart(2, '0');
  const ampm = hours >= 12 ? 'PM' : 'AM';
  hours = hours % 12;
  if (hours === 0) hours = 12;
  return { hour12: hours, minutes, ampm };
}

/* ─────────────────────────────────────────────
 * Media (same pattern as main post)
 * ───────────────────────────────────────────*/
const imageFileExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.bmp', '.svg', '.avif'];
const videoFileExtensions = ['.mp4', '.mov', '.m4v', '.webm', '.mkv', '.avi', '.ogv'];

const inferAttachmentKind = (att) => {
  const mime = String(att?.mimeType || att?.mime || att?.contentType || '').toLowerCase().trim();
  const fileType = String(att?.fileType || att?.type || '').toLowerCase().trim();
  const url = String(att?.url || att?.secureVideoUrl || att?.videoUrl || att?.secureImageUrl || att?.imageUrl || '').toLowerCase();

  if (mime.startsWith('video/') || fileType === 'video') return 'video';
  if (mime.startsWith('image/') || fileType === 'image') return 'image';
  if (videoFileExtensions.some((ext) => url.includes(ext))) return 'video';
  if (imageFileExtensions.some((ext) => url.includes(ext))) return 'image';
  if (att?.videoUrl || att?.secureVideoUrl || att?.thumbnailUrl || att?.posterUrl) return 'video';
  return 'image';
};

const mediaDisplayAllItems = computed(() => {
  const items = [];
  const atts = Array.isArray(props.post.attachments) ? props.post.attachments : [];

  for (const att of atts) {
    if (!att) continue;
    const fileId = att.fileId || att.file_id || att.mediaFileId || null;
    const kind = inferAttachmentKind(att);
    const imageSrc =
      (fileId ? `${apiProxyUrl}/api/image/${fileId}` : null) ||
      att.imageUrl ||
      att.secureImageUrl ||
      att.url ||
      null;
    const videoSrc = att.secureVideoUrl || att.videoUrl || att.url || null;
    const thumbnailSrc =
      att.thumbnailUrl ||
      att.posterUrl ||
      (fileId ? `${apiProxyUrl}/api/video-thumbnail/${fileId}` : null) ||
      imageSrc;

    if (kind === 'video') {
      const resolvedVideoSrc = videoSrc || imageSrc;
      if (!resolvedVideoSrc) continue;
      items.push({
        type: 'video',
        fileId: fileId ? String(fileId) : null,
        src: resolvedVideoSrc,
        thumbnailSrc: thumbnailSrc || imageSrc,
        poster: att.posterUrl || att.thumbnailUrl || null,
      });
    } else if (imageSrc) {
      items.push({
        type: 'image',
        fileId: fileId ? String(fileId) : null,
        src: imageSrc,
      });
    }
  }

  if (!items.length) {
    const cover =
      props.post.secureCoverImageUrl ||
      props.post.imageUrl ||
      props.post.coverImageUrl ||
      (props.post.coverMediaFileId
        ? `${apiProxyUrl}/api/image/${props.post.coverMediaFileId}`
        : null);

    if (cover) {
      items.push({
        type: 'image',
        fileId: props.post.coverMediaFileId ? String(props.post.coverMediaFileId) : null,
        src: cover,
      });
    }
  }

  return items;
});

const hasMedia = computed(() => mediaDisplayAllItems.value.length > 0);

const MAX_DISPLAY_TILES = 5;
const displayMediaItems = computed(() => mediaDisplayAllItems.value.slice(0, MAX_DISPLAY_TILES));
const extraMediaCount = computed(() =>
  mediaDisplayAllItems.value.length > MAX_DISPLAY_TILES
    ? mediaDisplayAllItems.value.length - MAX_DISPLAY_TILES
    : 0
);

const displayGridClass = computed(() => {
  const count = displayMediaItems.value.length;
  if (count === 1) return 'grid-cols-1';
  if (count === 2) return 'grid-cols-2';
  if (count === 3) return 'grid-cols-2 grid-rows-2';
  if (count === 4) return 'grid-cols-2 grid-rows-2';
  return 'grid-cols-3 grid-rows-2';
});

function displayTileClass(index) {
  const count = displayMediaItems.value.length;

  if (count === 1) return 'relative';
  if (count === 2) return 'relative aspect-[4/3]';

  if (count === 3) {
    if (index === 0) return 'relative row-span-2';
    return 'relative';
  }

  if (count === 4) return 'relative aspect-[4/3]';

  if (count >= 5) {
    if (index === 0) return 'relative col-span-2 aspect-[4/3]';
    if (index === 1) return 'relative';
    return 'relative aspect-[4/3]';
  }

  return 'relative';
}

const multiImageFitClass = computed(() => 'object-cover');

const firstVideoTileIndex = computed(() =>
  displayMediaItems.value.findIndex((item) => item?.type === 'video')
);
const activeInlineVideoTileIndex = ref(null);

function isInlinePlayingVideoTile(idx) {
  const item = displayMediaItems.value[idx];
  if (!item || item.type !== 'video') return false;
  if (activeInlineVideoTileIndex.value != null) {
    return Number(activeInlineVideoTileIndex.value) === Number(idx);
  }
  return firstVideoTileIndex.value === idx;
}

function playInlineVideoAt(idx) {
  const item = displayMediaItems.value[idx];
  if (!item || item.type !== 'video') return;
  activeInlineVideoTileIndex.value = idx;
}

function handleMediaTileClick(item, idx) {
  if (item?.type === 'video') {
    if (isInlinePlayingVideoTile(idx)) return;
    openViewerAt(idx);
    return;
  }
  openViewerAt(idx);
}

watch(
  () => displayMediaItems.value.map((item) => `${item?.type || ''}:${item?.fileId || item?.src || ''}`).join('|'),
  () => {
    activeInlineVideoTileIndex.value = null;
  }
);

/* ─────────────────────────────
 * Media viewer state (mirrors main post)
 * ─────────────────────────────*/
const viewerOpen = ref(false);
const viewerIndex = ref(0);

const viewerMediaItems = computed(() => {
  return mediaDisplayAllItems.value.map((item) => ({
    type: item.type,
    src: item.src,
    fileId: item.fileId || null,
    poster: item.poster || item.thumbnailSrc || null,
    thumbnailSrc: item.thumbnailSrc || null,
  }));
});

const openViewerAt = (idx) => {
  if (!viewerMediaItems.value.length) return;

  const max = viewerMediaItems.value.length - 1;
  const safeIndex = Math.min(Math.max(idx, 0), max);

  viewerIndex.value = safeIndex;
  viewerOpen.value = true;
};

const isSavingPost = ref(false);

async function handleSavePost(payload) {
  const idFromPayload = payload && payload.postId;
  const postId = idFromPayload || (props.post && props.post.id);

  if (!postId) {
    console.warn('[NewsfeedCreatorScheduledPost] handleSavePost: no postId found');
    return;
  }

  if (isSavingPost.value) return;
  isSavingPost.value = true;

  try {
    const res = await fetch(
      `${apiProxyUrl}/api/creator-newsfeed/posts/${encodeURIComponent(postId)}/save`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({}),
      }
    );

    if (!res.ok) {
      const text = await res.text().catch(() => '');
      console.error(
        '[NewsfeedCreatorScheduledPost] save-post backend NOT OK:',
        res.status,
        text
      );
      alert('Failed to save post. Please try again.');
      return;
    }

    const data = await res.json().catch(() => ({}));
    console.log('[NewsfeedCreatorScheduledPost] save-post OK:', data);

    // Let parent (Scheduled tab) update local saved state
    emit('save-post', { postId, backend: data });
  } catch (err) {
    console.error('[NewsfeedCreatorScheduledPost] save-post error:', err);
    alert('Error saving post. Please try again.');
  } finally {
    isSavingPost.value = false;
  }
}

function handleUnsavePost(payload) {
  const idFromPayload = payload && payload.postId;
  const postId = idFromPayload || (props.post && props.post.id);

  if (!postId) {
    console.warn('[NewsfeedCreatorScheduledPost] handleUnsavePost: no postId found');
    return;
  }

  // Just bubble up; parent will call backend
  emit('unsave-post', { postId });
}

function forwardDeletePost(payload) {
  console.log(
    '[NewsfeedCreatorScheduledPost] delete-post received from menu:',
    payload
  );
  emit('delete-post', payload);
}

// 🔹 Pinned state for scheduled post
const initialIsPinned = computed(() => {
  const p = props.post || {};

  if (typeof p.isPinned === 'boolean') return p.isPinned;
  if (typeof p.is_pinned === 'boolean') return p.is_pinned;
  if (typeof p.pinned === 'boolean') return p.pinned;

  if (typeof p.isPinned === 'number') return !!p.isPinned;
  if (typeof p.is_pinned === 'number') return !!p.is_pinned;

  return false;
});

const localIsPinned = ref(initialIsPinned.value);

const isPinned = computed(() => localIsPinned.value);

// Keep in sync if parent refreshes scheduledPosts array
watch(initialIsPinned, (val) => {
  localIsPinned.value = val;
});

const isPinningPost = ref(false);

async function handleTogglePinned(payload) {
  const idFromPayload = payload && payload.postId;
  const postId = idFromPayload || (props.post && props.post.id);

  if (!postId) {
    console.warn(
      '[NewsfeedCreatorScheduledPost] handleTogglePinned: no postId found'
    );
    return;
  }

  if (isPinningPost.value) return;
  isPinningPost.value = true;

  const currentlyPinned = isPinned.value;
  const shouldPin = !currentlyPinned;
  const method = shouldPin ? 'POST' : 'DELETE';

  try {
    const res = await fetch(
      `${apiProxyUrl}/api/creator-newsfeed/posts/${encodeURIComponent(
        postId
      )}/pin`,
      {
        method,
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      }
    );

    if (!res.ok) {
      const text = await res.text().catch(() => '');
      console.error(
        '[NewsfeedCreatorScheduledPost] pin-toggle backend NOT OK:',
        res.status,
        text
      );
      alert('Failed to update pin status. Please try again.');
      return;
    }

    const data = await res.json().catch(() => ({}));
    console.log('[NewsfeedCreatorScheduledPost] pin-toggle OK:', data);

    const newState =
      typeof data.isPinned === 'boolean' ? data.isPinned : shouldPin;

    // Update local state so the icon + menu label react instantly
    localIsPinned.value = newState;

    // Let parent tab know, so it can sync its scheduledPosts array
    emit('pin-post', { postId, isPinned: newState, backend: data });
  } catch (err) {
    console.error(
      '[NewsfeedCreatorScheduledPost] pin-toggle error:',
      err
    );
    alert('Error updating pin status. Please try again.');
  } finally {
    isPinningPost.value = false;
  }
}


</script>


